import { PrepRequestType } from 'models/prep-request.model';
import React, { useEffect, useState } from 'react';

import RequestStatusTag from 'components/base/request-status-tag';
import { Textarea } from '@cokitchen/cokitchen-components';

import { ENUM_REQUEST_STATUS } from 'models/common.model';
import PrepReportItem from './prep-report-item';
import CurrencyInput from 'components/base/currency-input';

type PrepReportBatchPayload = {
  batch_no: number;
  prep_ingredient_id: string;
  quantity_wasted: number;
  excess_quantity: number;
};

export type PrepReportItemPayload = {
  prep_request_item_id: string;
  quantity_produced: number;
  batches: PrepReportBatchPayload[];
  note: string;
};

const UpdatePrepReport = ({
  request,
  onUpdateItems
}: {
  request: PrepRequestType;
  onUpdateItems: (items: PrepReportItemPayload[]) => void;
}) => {
  const [showDetails, setShowDetails] = useState<boolean[]>([]);
  const [reportItemsById, setReportItemsById] = useState<Record<string, PrepReportItemPayload>>({});

  const toggleDetails = (index: number) => {
    setShowDetails((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const updateReportItem = (
    itemId: string,
    updater: (current: PrepReportItemPayload) => PrepReportItemPayload
  ) => {
    setReportItemsById((prev) => {
      const current =
        prev[itemId] ??
        ({
          prep_request_item_id: itemId,
          quantity_produced: 0,
          batches: [],
          note: ''
        } as PrepReportItemPayload);
      return {
        ...prev,
        [itemId]: updater(current)
      };
    });
  };

  const onItemChange = (batches: any[], prepIngredientId: string, itemId: string) => {
    updateReportItem(itemId, (current) => {
      const normalizedBatches = (batches ?? []).map((batch) => ({
        batch_no: Number(batch.batch_no || 0),
        prep_ingredient_id: prepIngredientId,
        quantity_wasted: Number(batch.quantity_wasted || 0),
        excess_quantity: Number(batch.excess_quantity || 0)
      }));

      const remaining = current.batches.filter(
        (batch) => batch.prep_ingredient_id !== prepIngredientId
      );

      return {
        ...current,
        batches: [...remaining, ...normalizedBatches]
      };
    });
  };

  useEffect(() => {
    if (!request.items?.length) return;
    setReportItemsById((prev) => {
      const next = { ...prev };
      let changed = false;
      request.items.forEach((item) => {
        if (!next[item.id]) {
          next[item.id] = {
            prep_request_item_id: item.id,
            quantity_produced: Number(item.quantity_produced || 0),
            batches: [],
            note: ''
          };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [request.items]);

  useEffect(() => {
    onUpdateItems(Object.values(reportItemsById));
  }, [reportItemsById, onUpdateItems]);

  return (
    <div className='px-2 md:px-2'>
      {request.items?.map((item, index) => (
        <div key={item.id}>
          <div className='flex flex-col sm:flex-row sm:justify-between border-b border-b-[#EAEBEB] bg-[#F7F7F7E5] px-3 md:px-4 py-4 md:py-4 gap-3 sm:gap-6 items-start sm:items-center'>
            <div className='flex-1'>
              <div className='font-semibold text-[#2E3031] flex items-center capitalize text-sm '>
                {item.ingredient?.name || 'Unknown Item'}
                {request.status === ENUM_REQUEST_STATUS.SENT ||
                request.status === ENUM_REQUEST_STATUS.RECEIVED ? (
                  <span className='ml-4'>
                    <RequestStatusTag status={item.status} />
                  </span>
                ) : null}
              </div>
              {request.status === ENUM_REQUEST_STATUS.SENT ||
              request.status === ENUM_REQUEST_STATUS.RECEIVED ? (
                <div className='flex gap-2 items-center text-xs text-[#6F7376]'>
                  <h4 className='font-medium text-[#2E3031] capitalize text-sm '>
                    {item.quantity_requested} {item.ingredient.unit_type || 'portion'} requested
                  </h4>
                </div>
              ) : (
                <h3 className='font-medium text-[#6F7376] capitalize text-xs md:text-sm'>
                  {item.quantity_requested} {item.item_type || item.ingredient?.unit_type}
                </h3>
              )}
            </div>

            {item.status === ENUM_REQUEST_STATUS.RECEIVED && (
              <div className='flex gap-2 md:gap-3 self-end sm:self-center'>
                {item.prep_item_ingredients && item.prep_item_ingredients.length > 0 && (
                  <button
                    className='rounded-full bg-white border px-2 md:px-4 py-1 text-[10px] md:text-xs border-[#EAEBEB]'
                    onClick={() => toggleDetails(index)}>
                    {showDetails[index] ? 'Hide' : 'Show'}
                  </button>
                )}
              </div>
            )}
          </div>

          {showDetails[index] && item.prep_item_ingredients.length > 0 && (
            <div>
              <div className='my-2 mx-4'>
                <CurrencyInput
                  label='Portions Produced  '
                  placeholder='00'
                  name='portion_produced'
                  hasUnit
                  unitValue={'units'}
                  disableUnit
                  hideCurrency
                  value={reportItemsById[item.id]?.quantity_produced ?? 0}
                  className=' text-sm'
                  onChange={(e) =>
                    updateReportItem(item.id, (current) => ({
                      ...current,
                      quantity_produced: Number(e) || 0
                    }))
                  }
                />
              </div>

              {item.prep_item_ingredients.map((prepItem, itemIndex) => (
                <div key={itemIndex}>
                  <PrepReportItem
                    onChange={(reportItem) => onItemChange(reportItem, prepItem.id, item.id)}
                    item={prepItem}
                  />
                </div>
              ))}

              <div className='my-2 mx-4'>
                <Textarea
                  name='note'
                  label='Note (optional)'
                  placeholder='Add a note'
                  value={reportItemsById[item.id]?.note ?? ''}
                  onChange={(e) =>
                    updateReportItem(item.id, (current) => ({
                      ...current,
                      note: e.target.value ?? ''
                    }))
                  }
                  className='w-full !border-b-[2px] !border-b-[#D5D6D7] bg-[#F4F5F5] !rounded-none h-[94px]'
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default UpdatePrepReport;
